import { Button, Dialog, Input } from "@app/ui";
import { useEffect, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { useIcsImportStore } from "../../stores/index.js";

/** After .ics file(s) are picked, choose where the imported items land:
 *  a new calendar, or merged into an existing one. */
export function IcsImportDialog() {
  const { t } = useI18n();
  const { open, pending, resolve } = useIcsImportStore();
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [name, setName] = useState("");
  const [calendarId, setCalendarId] = useState("");

  useEffect(() => {
    if (!pending) return;
    setName(pending.suggestedName);
    setMode("new");
    setCalendarId(pending.calendars[0]?.id ?? "");
  }, [pending]);

  if (!pending) return null;

  const fileLabel = pending.fileCount > 1 ? t("ics.files", { count: pending.fileCount }) : t("ics.file");
  const itemNoun = pending.count === 1 ? t("ics.item") : t("ics.items");
  const canConfirm = mode === "new" ? name.trim().length > 0 : calendarId.length > 0;

  return (
    <Dialog open={open} onClose={() => resolve({ kind: "cancel" })} title={t("ics.importTitle")} className="max-w-md">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {t("ics.importSummary", { count: pending.count, itemNoun, fileLabel })}
        </p>

        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="ics-target" className="mt-1" checked={mode === "new"} onChange={() => setMode("new")} />
          <span className="flex-1">
            <span className="font-medium">{t("ics.createNewCalendar")}</span>
            <Input
              className="mt-1.5"
              value={name}
              disabled={mode !== "new"}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("ics.calendarName")}
            />
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="ics-target"
            className="mt-1"
            checked={mode === "existing"}
            disabled={!pending.calendars.length}
            onChange={() => setMode("existing")}
          />
          <span className="flex-1">
            <span className="font-medium">{t("ics.addExistingCalendar")}</span>
            <select
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              value={calendarId}
              disabled={mode !== "existing" || !pending.calendars.length}
              onChange={(e) => setCalendarId(e.target.value)}
            >
              {pending.calendars.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => resolve({ kind: "cancel" })}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            disabled={!canConfirm}
            onClick={() => resolve(mode === "new" ? { kind: "new", name: name.trim() } : { kind: "existing", calendarId })}
          >
            {t("common.import")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
